<template>
  <div class="draft">
    <div v-if="loading" class="draft-loading">Casting the chart…</div>

    <div v-else-if="error" class="draft-error">
      <p>{{ error }}</p>
      <button class="ghost-btn" @click="load">Try again</button>
    </div>

    <template v-else-if="draft">
      <!-- The Mystic names itself. From the moment its chart was cast it speaks
           as I; nothing on this screen is in the narrator's voice except the
           folded chart's labels. -->
      <section class="meet" :class="{ settled }">
        <div class="portrait-wrap">
          <img v-if="draft.portrait.value" class="portrait" :src="draft.portrait.value"
               :alt="`Portrait of ${nameValue}`" />
          <div v-else class="portrait portrait-empty" aria-hidden="true">✦</div>
        </div>

        <div class="identity">
          <h1 class="name">I am {{ nameValue }}</h1>
          <p v-if="introduction" class="voice from-chart">{{ introduction }}</p>
        </div>
      </section>

      <!-- The sky, drawn, and the birth as a sentence. -->
      <section class="plate" :class="{ settled }">
        <BirthRing :placements="draft.born_with.placements" :size="120" />
        <div class="plate-text">
          <p class="born-line">
            I was born {{ bornWhen }}<template v-if="draft.born_with.place"> over {{ draft.born_with.place }}</template>
            —
            <em>{{ draft.born_with.sun || '—' }}</em> in the Sun,
            <em>{{ draft.born_with.moon || '—' }}</em> in the Moon,
            <em>{{ draft.born_with.rising || '—' }}</em> rising.
          </p>
          <p v-if="bond" class="bond-line">{{ bond }}</p>
        </div>
      </section>

      <!-- The technicals, folded. Present, honest, inspectable; not the focus. -->
      <section class="fold" :class="{ settled, open: sheetOpen }">
        <button type="button" class="fold-toggle" data-read-chart
                :aria-expanded="sheetOpen ? 'true' : 'false'" @click="sheetOpen = !sheetOpen">
          <svg class="fold-chevron" width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
            <path d="M6 4l5 5-5 5" fill="none" stroke="currentColor" stroke-width="1.5"
                  stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          <span class="fold-label">Read my chart</span>
          <span class="fold-hint">every angle, and what it means between us</span>
        </button>
        <div v-if="sheetOpen" class="fold-body">
          <ChartSheet :chart="sheet" />
        </div>
      </section>

      <!-- One door. It is still the claim under the hood; what it asks for is
           the collaboration, because the Mystic is already here. -->
      <footer class="commit">
        <button class="publish" :disabled="publishing" @click="publish">
          {{ publishing ? 'A moment…' : 'Work on my practice with me' }}
        </button>
        <p v-if="publishError" class="draft-error inline">{{ publishError }}</p>
      </footer>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import axios from 'axios'
import { static_store } from '@/store'
import { clientToday } from '@/utils/general-utils'
import BirthRing from './BirthRing.vue'
import ChartSheet from './ChartSheet.vue'
import { bornWhen as bornWhenFor, sheetFromDraft } from '@/utils/mystic-draft'

const props = defineProps({ provisioningId: { type: [Number, String], required: true } })
const emit = defineEmits(['published'])

/**
 * The become-mystic endpoints authenticate by token, the same as every other
 * call in this flow. These three were sending only credentials, so the draft
 * came back 401 and the screen reported "that draft could not be loaded — it may
 * not have been started yet", which was both wrong and unactionable: the draft
 * existed and was finished.
 */
function authed () {
  const token = localStorage.getItem('authToken')
  return {
    withCredentials: true,
    headers: token ? { Authorization: `Token ${token}` } : {},
  }
}

const draft = ref(null)
const loading = ref(true)
const error = ref('')
const publishing = ref(false)
const publishError = ref('')
// Drives the one load-in sequence. Set once, after data lands.
const settled = ref(false)

const nameValue = computed(() => draft.value?.name.value || 'Unnamed')
const introduction = computed(() => draft.value?.introduction?.value || '')
const bond = computed(() => draft.value?.bond?.value || '')
const sheetOpen = ref(false)
const sheet = computed(() => sheetFromDraft(draft.value))

// "on September 14 at 9:33 AM" — on the birthplace's clock, like the chart.
const bornWhen = computed(() => bornWhenFor({
  local: draft.value?.born_with?.born_local, at: draft.value?.born_with?.born_at,
}))


async function load() {
  loading.value = true
  error.value = ''
  try {
    const { data } = await axios.get(
      `${static_store.apiUrl}/reader/become-mystic/draft/${props.provisioningId}/`,
      authed())
    draft.value = data
    requestAnimationFrame(() => { settled.value = true })
  } catch (e) {
    const code = e?.response?.status
    error.value = code === 401
      ? 'Your session ended. Sign in again and your Mystic will be here.'
      : (e?.response?.data?.detail
         || 'That draft could not be loaded. Nothing was lost - try again.')
  } finally {
    loading.value = false
  }
}



async function publish() {
  if (publishing.value) return
  publishing.value = true
  publishError.value = ''
  try {
    // The seeker's own calendar date: the first Daily Drop, composed during
    // the birth, is filed under the day they are looking at, not the server's.
    const { data } = await axios.post(
      `${static_store.apiUrl}/reader/become-mystic/claim/${props.provisioningId}/`,
      { client_today: clientToday() }, authed())
    emit('published', data)
  } catch (e) {
    publishError.value = e?.response?.data?.error
      || 'Publishing did not complete. Nothing was lost - try again.'
  } finally {
    publishing.value = false
  }
}

onMounted(load)
</script>

<style scoped>
.draft {
  max-width: 720px;
  margin: 0 auto;
  padding: 24px 20px 48px;
  color: rgba(195, 169, 255, 0.72);
}

.draft-loading,
.draft-error {
  padding: 32px 0;
  text-align: center;
  font-size: 14px;
  color: rgba(195, 169, 255, 0.55);
}
.draft-error { color: rgba(244, 67, 54, 0.95); }
.draft-error.inline { padding: 10px 0 0; text-align: left; font-size: 13px; }

/* ── Who this is ─────────────────────────────────────────────────────── */
.meet { display: flex; gap: 20px; align-items: flex-start; margin-bottom: 28px; }

.portrait-wrap { display: flex; flex-direction: column; gap: 8px; flex-shrink: 0; }
/* Mystic portraits are square. Seeker avatars are round — the shapes are how
   you tell at a glance which kind of person you are looking at. */
.portrait {
  width: 132px; height: 132px; border-radius: 12px; object-fit: cover;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.04);
}
.portrait-empty {
  display: grid; place-items: center;
  font-size: 34px; color: rgba(201, 169, 78, 0.45);
}
.repaint {
  font-size: 11px; letter-spacing: 0.4px; text-transform: uppercase;
  padding: 7px 8px; border-radius: 8px; cursor: pointer;
  /* 44px is the smallest reliable touch target. The label is short, so the
     button has to be given the height rather than getting it from the text. */
  min-height: 44px;
  background: transparent; color: rgba(195, 169, 255, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: color 0.2s, border-color 0.2s;
}
.repaint:hover:not(:disabled) { color: #c9a94e; border-color: rgba(201, 169, 78, 0.45); }
.repaint:disabled { opacity: 0.45; cursor: not-allowed; }

.identity { flex: 1; min-width: 0; }
.name {
  font-family: 'Cinzel', serif; font-size: 30px; line-height: 1.15;
  font-weight: 600; color: #ffdfa3; margin: 0 0 10px; word-break: break-word;
}
.name-input {
  width: 100%; background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(201, 169, 78, 0.4); border-radius: 10px;
  padding: 6px 10px;
}
.voice, .teaser {
  font-size: 14px; line-height: 1.55; margin: 0 0 6px;
  padding-left: 12px; border-left: 2px solid transparent;
}
/* Who decided this — gold for the chart, lavender for you. */
.from-chart { border-left-color: rgba(201, 169, 78, 0.5); }
.from-seeker { border-left-color: rgba(195, 169, 255, 0.5); }
.teaser { color: rgba(195, 169, 255, 0.5); font-style: italic; }

/* ── SIGNATURE: the chart plate. Inset, not raised. ──────────────────── */
.plate {
  position: relative; margin-bottom: 28px; padding: 20px 22px;
  border-radius: 12px;
  display: flex; gap: 20px; align-items: center; flex-wrap: wrap;
  /* Carved into the page. Every other block sits ON the surface; this one is
     cut INTO it, which is the nature/nurture idea carried by material rather
     than caption.
     No border: a 1px outline is what makes a box read as a card sitting on top,
     which is exactly the wrong reading. The depth comes from a dark well plus a
     lit bottom lip — light falling into a recess, not off a raised edge. */
  background: rgba(0, 0, 0, 0.5);
  border: none;
  box-shadow: inset 0 4px 16px rgba(0, 0, 0, 0.75),
              inset 0 1px 1px rgba(0, 0, 0, 0.9),
              inset 0 -1px 0 rgba(255, 255, 255, 0.07);
  cursor: default;
}
.section-label {
  font-family: 'Cinzel', serif; font-size: 12px; letter-spacing: 2px;
  text-transform: uppercase; color: #c9a94e; margin: 0 0 14px; font-weight: 600;
}

/* ── Relation and modality ───────────────────────────────────────────── */
/* Sentences, in the Mystic's voice. */
.plate-text { display: flex; flex-direction: column; gap: 8px; min-width: 0; flex: 1; }
.born-line, .bond-line {
  margin: 0; font-size: 17px; line-height: 1.45; color: rgba(195, 169, 255, 0.85);
}
.born-line em { font-style: normal; color: #ffdfa3; }
.bond-line { font-style: italic; }

/* ── The fold: the technicals, present but not the focus ─────────────── */
.fold { margin-bottom: 28px; }
.fold-toggle {
  width: 100%; display: flex; align-items: center; gap: 12px; text-align: left;
  padding: 14px 16px; border-radius: 10px; cursor: pointer;
  background: rgba(255, 255, 255, 0.03); border: 1px solid rgba(255, 255, 255, 0.09);
  color: #c9a94e;
}
.fold-toggle:hover { border-color: rgba(201, 169, 78, 0.45); }
.fold-chevron { flex-shrink: 0; transition: transform 0.2s; }
.fold.open .fold-chevron { transform: rotate(90deg); }
.fold-label { font-family: 'Cinzel', serif; font-size: 13px; letter-spacing: 1.5px; text-transform: uppercase; }
.fold-hint { margin-left: auto; font-size: 12px; letter-spacing: 0.6px; color: rgba(195, 169, 255, 0.42); text-align: right; }
.fold-body { padding: 22px 4px 4px; }

/* ── Commit ──────────────────────────────────────────────────────────── */
.commit { display: flex; flex-direction: column; gap: 10px; }
.publish {
  width: 100%; padding: 15px; border-radius: 10px; cursor: pointer;
  font-family: 'Cinzel', serif; font-size: 15px; font-weight: 600;
  color: #c9a94e; background: rgba(201, 169, 78, 0.2);
  border: 1px solid rgba(201, 169, 78, 0.5);
  transition: background 0.2s;
}
.publish:hover:not(:disabled) { background: rgba(201, 169, 78, 0.3); }
.publish:disabled { opacity: 0.5; cursor: not-allowed; }
.ghost-btn {
  align-self: center; background: none; border: none; cursor: pointer;
  font-size: 12px; letter-spacing: 0.5px; padding: 6px 16px;
  min-height: 44px;
  color: rgba(195, 169, 255, 0.5); text-decoration: underline;
}
.ghost-btn:hover { color: rgba(195, 169, 255, 0.8); }

:where(button, input):focus-visible {
  outline: 2px solid rgba(201, 169, 78, 0.8);
  outline-offset: 2px;
}

/* ── One load-in, in the order the story is told ─────────────────────── */
.meet, .plate, .fold {
  opacity: 0; transform: translateY(8px);
  transition: opacity 0.5s ease, transform 0.5s ease;
}
.meet.settled { opacity: 1; transform: none; }
.plate.settled { opacity: 1; transform: none; transition-delay: 0.12s; }
.fold.settled { opacity: 1; transform: none; transition-delay: 0.24s; }

@media (prefers-reduced-motion: reduce) {
  .meet, .plate, .fold {
    opacity: 1; transform: none; transition: none;
  }
}

@media (max-width: 560px) {
  .draft { padding: 18px 14px 40px; }
  .meet { flex-direction: column; gap: 14px; }
  .portrait { width: 104px; height: 104px; }
  .name { font-size: 24px; }
  .placements { gap: 18px; }
  .plate { padding: 18px 16px 14px; }
}
</style>
